import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { ControlErrorDirective } from '../../../../shared/directive/control-error.directive';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { WyznaczAdresataResponse } from '../../../../shared/models/wyznacz-adresata-response';
import { Application } from '../../../../shared/models/application';
import { PocztaWewnApi } from '../../../../shared/services/api/poczta-wewn.api';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SlownikiApi } from '../../../../shared/services/api/slowniki.api';
import { SlownikEnum } from '../../../../shared/enums/slownik.enum';
import { Slownik } from '../../../../shared/models/slownik';
import { Select, SelectChangeEvent } from 'primeng/select';
import { RadioButton } from 'primeng/radiobutton';
import { AutoFocus } from 'primeng/autofocus';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ApplicationCode } from '../../../../shared/enums/application_code';
import { TypInstytucji } from '../../../../shared/enums/typ-instytucji.enum';
import { InfoMessageComponent } from '../../../../shared/components/info-message/info-message.component';
import { AuthService } from '../../../../core/services/auth.service';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-application-choose-jednostka',
  imports: [
    ControlErrorDirective,
    FormsModule,
    InputText,
    ReactiveFormsModule,
    Button,
    TableModule,
    Select,
    RadioButton,
    AutoFocus,
    InfoMessageComponent,
  ],
  templateUrl: './application-choose-jednostka-dialog.html',
  styleUrl: './application-choose-jednostka-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PocztaWewnApi],
})
export class ApplicationChooseJednostkaDialog implements OnInit {
  protected readonly dynamicDialogRef = inject(DynamicDialogRef);
  readonly #wyznaczAdresataApi = inject(PocztaWewnApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #slownikApi = inject(SlownikiApi);
  readonly #destroyRef = inject(DestroyRef);
  readonly #authService = inject(AuthService);
  readonly user = computed(() => this.#authService.getUser());

  protected application = input.required<Application>();
  protected obszarZSkod = input.required<string>();
  protected filter = model<string>('');
  protected rows = signal<WyznaczAdresataResponse[]>([]);
  protected selectedItem = model<WyznaczAdresataResponse | null>();
  protected voivodeshipList = signal<Slownik[]>([]);
  protected dataLoading = signal<boolean>(false);
  protected hasActiveSearch = signal<boolean>(false);

  ngOnInit(): void {
    if (this.application().czyJednostkaWojewodzka()) {
      this.#initializeWojewodztwaList();
    } else {
      this.#initializeSearchByMiejscowoscBeneficjenta();
    }
  }

  protected onFilterGminy(initialSearch?: boolean): void {
    if (this.filter()?.length >= 3 || initialSearch) {
      this.hasActiveSearch.set(true);
      this.dataLoading.set(true);
      this.#wyznaczAdresataApi
        .wyznaczAdresataWgGminy(
          this.application().kod!,
          this.obszarZSkod(),
          initialSearch
            ? this.user()?.adres?.adres?.miejscowosc?.nazwa!
            : this.filter()!,
        )
        .pipe(
          tap((response: WyznaczAdresataResponse[]) => {
            this.rows.set(response);
          }),
          catchError((err) => {
            this.#httpResponseService.showErrorMessage(
              err,
              'Wystąpił błąd podczas pobierania danych',
            );
            return EMPTY;
          }),
          finalize(() => this.dataLoading.set(false)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected onFilterWojewodztwa(voivodeship: SelectChangeEvent): void {
    this.resetFilter();
    let kodTypuInstyt;
    switch (this.application().kod) {
      case ApplicationCode.ON_1:
      case ApplicationCode.RKZ_6:
      case ApplicationCode.RKZ_7:
        kodTypuInstyt = TypInstytucji.WOJEWODA;
        break;
      case ApplicationCode.ON_2:
        kodTypuInstyt = TypInstytucji.MARSZALEK;
        break;
      case ApplicationCode.PPW:
        kodTypuInstyt = TypInstytucji.WZOON;
        break;
    }
    this.dataLoading.set(true);
    this.#wyznaczAdresataApi
      .wyznaczAdresataWgWojewodztwa(
        voivodeship.value.id,
        this.obszarZSkod(),
        kodTypuInstyt,
      )
      .pipe(
        tap((response: WyznaczAdresataResponse[]) => {
          this.rows.set(response);
          this.hasActiveSearch.set(true);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania danych',
          );
          return EMPTY;
        }),
        finalize(() => this.dataLoading.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected resetFilter(): void {
    this.filter.set('');
    this.rows.set([]);
    this.hasActiveSearch.set(false);
    this.selectedItem.set(null);
  }

  #initializeWojewodztwaList(): void {
    this.#slownikApi
      .dajListePozycjiSlownika$({ kodSlownika: SlownikEnum.TERYT_WOJ })
      .pipe(
        tap((data: Slownik[]) => {
          this.voivodeshipList.set(data);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania listy województw',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected emitSelectedApplication(): void {
    this.dynamicDialogRef.close(this.selectedItem());
  }

  #initializeSearchByMiejscowoscBeneficjenta() {
    if (
      !this.user()?.adres?.czyAdresZagraniczny &&
      this.user()?.adres?.adres?.miejscowosc?.nazwa!
    ) {
      this.onFilterGminy(true);
    }
  }

  protected onRowSelect(event: any) {
    this.selectedItem.set(event?.data);
  }
}
